<template>
  <div class="patient-definition">
    <div class="hvh-flex wrap center mb-4">
      <div class="col-12 col-md-6 col-lg-5 pr-md-4">
        <div class="mr-3">
          <p class="definition-title mb-2">Clinical Criteria</p>
          <p>Input the key criteria which identifies patients for this cohort. Criteria can include medications, procedures, as well as diagnosis codes, and they can be grouped together to ease analytic readability.</p>
          <p class="criteria-selection">
            <span class="clickable" @click="useAdvancedCriteria = !isAdvancedCriteria">
              {{ isAdvancedCriteria ? 'Simple Criteria' : 'Advanced Criteria' }}
            </span>
          </p>
          <CohortGroupCriteria :value="inclusionCriteria" :isAdvancedCriteria="isAdvancedCriteria" :cohorts="predefinedCohorts" @input="emitValue({ inclusionCriteria: JSON.stringify($event) })" />

          <div class="mt-4" v-if="isAdvancedCriteria">
            <p class="definition-title mb-2">Advanced Options</p>
            <p class="mb-3">
              Advanced options enable settings to require a specific length of time before or after the cohort criteria is met. As an example, requiring a patient to have records for at least 12 months before a diagnosis code is identified, enables
              analytics on patients with 1 year of records before they were diagnosed.
            </p>
            <CohortTimeCriteria class="mb-3" :value="cohort.timeBeforeCriteria" label="Enrollment time before meeting criteria" @input="emitValue({ timeBeforeCriteria: $event })" />
            <CohortTimeCriteria class="mb-3" :value="cohort.timeAfterCriteria" label="Enrollment time after meeting criteria" @input="emitValue({ timeAfterCriteria: $event })" />
            <CohortTimeCriteria class="mb-3" :value="cohort.enrollmentTime" label="Minimum enrollment time" @input="emitValue({ enrollmentTime: $event })" />
          </div>
        </div>
      </div>
      <div class="splitter col-12 col-md-6 col-lg-5 pl-md-4 py-0">
        <div class="ml-3">
          <p class="definition-title mb-2">Demographics</p>
          <p>Further refine the patients included within a cohort by identifying demographic requirements such as age, region, gender, or insurance type.</p>

          <div class="filters">
            <p class="small-title mb-0">Age Range</p>
            <CohortRangeFilter class="mb-4" v-model="minMaxAge" :min="ageRange[0]" :max="ageRange[1]" />

            <p class="small-title mb-0">Year Range</p>
            <CohortRangeFilter class="mb-4" v-model="minMaxYear" :min="yearRange[0]" :max="yearRange[1]" />

            <p class="small-title mb-1">State(s)</p>
            <CohortStateSelectFilter class="mt-2 pt-0 mb-4" :value="cohort.states" @input="emitValue({ states: $event })" />

            <p class="small-title mb-1">Gender</p>
            <CohortCheckBoxFilter class="mt-2 pt-0 mb-4" :value="cohort.gender" :items="genders" @input="emitValue({ gender: $event })" />

            <p class="small-title mb-1">Insurance Type</p>
            <CohortCheckBoxFilter class="mt-2 pt-0 mb-4" :value="cohort.insuranceTypes" :items="options.availableInsuranceTypes" itemValue="id" itemLabel="name" @input="emitValue({ insuranceTypes: $event })" />
          </div>
        </div>
      </div>
    </div>

    <div class="text-center">
      <v-btn flat large class="hvh-btn hvh-btn--primary m-0" :class="{ disabled: !saveEnabled }" @click="$emit('save')">Save</v-btn>
    </div>
  </div>
</template>

<script>
import eqNode from 'eqnode';
import CohortCheckBoxFilter from '../../../../components/analytics/cohorts/cohortFilters/CohortCheckBoxFilter.vue';
import CohortRangeFilter from '../../../../components/analytics/cohorts/cohortFilters/CohortRangeFilter.vue';
import CohortStateSelectFilter from '../../../../components/analytics/cohorts/cohortFilters/CohortStateSelectFilter.vue';
import CohortGroupCriteria from '../../../../components/cohorts/criteria/CohortGroupCriteria.vue';
import CohortTimeCriteria from '../../../../components/cohorts/criteria/CohortTimeCriteria.vue';
import { getDemographics } from '../../../../libraries/cohorts';
import { customNumberFormat, hashCode } from '../../../../libraries/common';
export default {
  components: { CohortRangeFilter, CohortStateSelectFilter, CohortCheckBoxFilter, CohortGroupCriteria, CohortTimeCriteria },
  title: 'Cohort - Patient Definition',
  props: ['cohort', 'originalCohort', 'value', 'predefinedCohorts', 'options'],
  data() {
    var demographics = getDemographics();

    return {
      ageRange: demographics.ageRange,
      yearRange: demographics.years,
      genders: demographics.genders,
      ages: demographics.ages,
      useAdvancedCriteria: undefined,
    };
  },
  computed: {
    inclusionCriteria() {
      var inclusionCriteria = JSON.parse(this.cohort?.inclusionCriteria || '[]');
      inclusionCriteria.children.forEach(item => item.children.forEach(child => child.id = child.id || Math.random()));
      return inclusionCriteria;
    },
    isAdvancedCriteria() {
      if (this.useAdvancedCriteria !== undefined)
        return this.useAdvancedCriteria;

      return eqNode.fromObject(this.inclusionCriteria).find(f => f.item.occurancesNum > 1)
        || eqNode.fromObject(this.exclusionCriteria || {}).find(f => f.item.occurancesNum > 1)
        || this.cohort.timeBeforeCriteria
        || this.cohort.timeAfterCriteria
        || this.cohort.enrollmentTime;
    },
    minMaxAge: {
      get() {
        return [this.cohort.minAge, this.cohort.maxAge];
      },
      set([minAge, maxAge]) {
        this.emitValue({ minAge, maxAge });
      },
    },
    minMaxYear: {
      get() {
        return [this.cohort.minYear, this.cohort.maxYear];
      },
      set([minYear, maxYear]) {
        this.emitValue({ minYear, maxYear });
      },
    },
    saveEnabled() {
      return hashCode(this.getCohortDefinitionData(this.cohort)) !== hashCode(this.getCohortDefinitionData(this.originalCohort));
    },
  },
  methods: {
    emitValue(obj) {
      this.$emit('update:cohort', { ...this.cohort, ...obj });
    },
    getCohortDefinitionData(item) {
      return ['inclusionCriteria', 'timeBeforeCriteria', 'timeAfterCriteria', 'enrollmentTime',
        'minAge', 'maxAge', 'minYear', 'maxYear', 'states', 'gender', 'insuranceTypes']
        .reduce((acc, key) => { acc[key] = item[key]; return acc; }, {});
    },
    customNumberFormat,
  },
  mounted() {
    if (process.env.NODE_ENV === 'development') window['thisCohortPatientDefinition'] = this;
  },
}
</script>

<style lang="scss" scoped>
.patient-definition {
  padding: 80px;
  .definition-title {
    color: #04051f;
    font-size: 18px;
    font-weight: 600;
  }
  .small-title {
    color: #181815;
    font-size: 14px;
    font-weight: 600;
  }
  .filters {
    max-width: 320px;
  }
  .criteria-selection {
    color: #002f6c;
    text-decoration: underline;
    text-align: right;
  }
}
@media (min-width: $breakingPointMd) {
  .splitter {
    border-left: 1px solid #d2d2d4;
  }
}
</style>